import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import {
  STATUSES, STATUS_LABEL, STATUS_STYLES, APPLIANCE_TYPES, JOB_TYPES, MANUFACTURERS, AD_SOURCES, findLabel, formatDateTime
} from "@/lib/options";
import { ChevronLeft, Pencil, Phone, Mail, MapPin, Trash2, Upload, X as XIcon, FileText, Send, MessageSquare, Truck, CreditCard, Smartphone, CheckCircle2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ChargeOnReaderDialog } from "@/components/tickets/ChargeOnReaderDialog";
import { JobHistory } from "@/components/customers/JobHistory";
import { History, Flag } from "lucide-react";
import { FollowupPanel } from "@/components/tickets/FollowupPanel";
import { PartsPanel } from "@/components/tickets/PartsPanel";
import { Package } from "lucide-react";

export default function TicketDetail() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [t, setT] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [emailNote, setEmailNote] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [smsBody, setSmsBody] = useState("");
  const [sendingSms, setSendingSms] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [readerOpen, setReaderOpen] = useState(false);

  const loadOnTheWayTemplate = async () => {
    setLoadingTemplate(true);
    try {
      const res = await api.get(`/tickets/${id}/sms/on-the-way-template`);
      setSmsBody(res.data.body);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingTemplate(false);
    }
  };

  const sendSms = async () => {
    if (!smsBody.trim()) {
      toast.error("Message can't be empty");
      return;
    }
    setSendingSms(true);
    try {
      const res = await api.post(`/tickets/${id}/sms`, { body: smsBody });
      toast.success(`SMS sent to ${res.data.sent_to}`);
      setSmsBody("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingSms(false);
    }
  };

  const sendConfirmationEmail = async () => {
    setSendingEmail(true);
    try {
      const res = await api.post(`/tickets/${id}/send-confirmation`, { note: emailNote });
      toast.success(`Confirmation sent to ${res.data.sent_to}`);
      setEmailNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingEmail(false);
    }
  };

  const uploadAttachments = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await fetch(`${API}/tickets/${id}/attachments`, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || `Upload failed (${res.status})`);
        }
      }
      toast.success(`Uploaded ${files.length} photo${files.length > 1 ? "s" : ""}`);
      load();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deleteAttachment = async (attId) => {
    try {
      await api.delete(`/tickets/${id}/attachments/${attId}`);
      toast.success("Photo deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const teamLookup = useMemo(() => {
    const m = {};
    for (const tech of technicians) m[tech.id] = tech;
    return m;
  }, [technicians]);

  const load = useCallback(async () => {
    try {
      const [tk, tech] = await Promise.all([
        api.get(`/tickets/${id}`),
        api.get("/technicians"),
      ]);
      setT(tk.data);
      setTechnicians(tech.data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/tickets");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Handle Square POS return: parse callback query params, POST to backend, clean URL.
  useEffect(() => {
    if (searchParams.get("square_pos") !== "1") return;
    const clientTx = searchParams.get("com.squareup.pos.CLIENT_TRANSACTION_ID");
    const serverTx = searchParams.get("com.squareup.pos.SERVER_TRANSACTION_ID");
    const errCode = searchParams.get("com.squareup.pos.ERROR_CODE");
    const errDesc = searchParams.get("com.squareup.pos.ERROR_DESCRIPTION");
    const meta = searchParams.get("com.squareup.pos.REQUEST_METADATA");

    // Clear the URL params immediately so a refresh doesn't re-fire the POST
    const next = new URLSearchParams(searchParams);
    ["square_pos", "com.squareup.pos.CLIENT_TRANSACTION_ID", "com.squareup.pos.SERVER_TRANSACTION_ID",
     "com.squareup.pos.ERROR_CODE", "com.squareup.pos.ERROR_DESCRIPTION",
     "com.squareup.pos.REQUEST_METADATA"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });

    // If Square didn't send back either a transaction id or an error, the tech probably
    // opened the launch URL and backed out — nothing to record.
    if (!clientTx && !serverTx && !errCode) return;

    (async () => {
      try {
        const { data } = await api.post(`/tickets/${id}/square/pos-callback`, {
          client_transaction_id: clientTx,
          server_transaction_id: serverTx,
          error_code: errCode,
          error_description: errDesc,
          request_metadata: meta,
        });
        if (data.status === "paid") {
          toast.success("Payment received on Square Reader");
        } else {
          toast.error(`Square Reader: ${errDesc || errCode || "payment did not complete"}`);
        }
        load();
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateStatus = async (status) => {
    try {
      await api.patch(`/tickets/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/tickets/${id}`);
      toast.success("Ticket deleted");
      navigate("/tickets");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!t) return <div className="text-slate-500" data-testid="loading-ticket">Loading…</div>;

  const c = t.customer || {};

  return (
    <div className="space-y-6" data-testid="ticket-detail-root">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/tickets">
          <Button variant="ghost" size="sm" className="gap-1" data-testid="back-btn"><ChevronLeft className="w-4 h-4" /> Back</Button>
        </Link>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <Button
                onClick={() => setReaderOpen(true)}
                className="bg-emerald-700 hover:bg-emerald-800 gap-2"
                data-testid="charge-reader-btn"
              >
                <Smartphone className="w-4 h-4" /> Charge on Reader
              </Button>
              <Link to={`/tickets/${id}/edit?scroll=payment`}>
                <Button variant="outline" className="border-blue-300 text-blue-800 hover:bg-blue-50 gap-2" data-testid="take-payment-btn">
                  <CreditCard className="w-4 h-4" /> Take Payment
                </Button>
              </Link>
            </>
          )}
          <Link to={`/tickets/${id}/edit`}>
            <Button variant="outline" className="gap-2" data-testid="edit-ticket-btn"><Pencil className="w-4 h-4" /> Edit</Button>
          </Link>
          {isAdmin && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-red-700 border-red-200 hover:bg-red-50 gap-2" data-testid="delete-ticket-btn">
                <Trash2 className="w-4 h-4" /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this ticket?</AlertDialogTitle>
                <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            {findLabel(APPLIANCE_TYPES, t.appliance_type)} · {findLabel(JOB_TYPES, t.job_type)}
          </h1>
          <Badge variant="outline" className={`${STATUS_STYLES[t.status]} rounded-full`} data-testid="status-badge">
            {STATUS_LABEL[t.status]}
          </Badge>
          {t.square_pos_paid_at && (
            <Badge
              variant="outline"
              className="rounded-full bg-emerald-50 text-emerald-800 border-emerald-300 gap-1"
              data-testid="reader-paid-badge"
              title={`Server txn: ${t.square_pos_server_transaction_id || "—"}`}
            >
              <CheckCircle2 className="w-3 h-3" />
              Reader paid
              {typeof t.square_pos_amount_cents === "number" &&
                ` · $${(t.square_pos_amount_cents / 100).toFixed(2)}`}
            </Badge>
          )}
        </div>
        <p className="text-base text-slate-500 mt-1">Ticket ID: <span className="font-mono font-bold text-slate-900">{t.id.slice(0, 8)}</span></p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="max-lg:contents lg:col-span-2 lg:flex lg:flex-col lg:gap-4 lg:order-1">
          <Card className="border-slate-200 max-lg:order-4 lg:order-2">
            <CardContent className="p-6 space-y-4">
              <h2 className="font-heading text-lg font-bold text-slate-900">Job</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <Field label="Job Type" value={findLabel(JOB_TYPES, t.job_type)} />
                <Field label="Ad Source" value={findLabel(AD_SOURCES, t.ad_source)} />
                <Field label="Status" value={STATUS_LABEL[t.status]} />
                <Field label="Scheduled" value={formatDateTime(t.scheduled_at)} />
                <Field label="Created" value={formatDateTime(t.created_at)} />
                <Field label="Updated" value={formatDateTime(t.updated_at)} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Description</div>
                <div className="text-sm text-slate-900 whitespace-pre-wrap" data-testid="ticket-description">
                  {t.job_description || <span className="text-slate-400">No description</span>}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Problem notes</div>
                <div className="text-sm text-slate-900 whitespace-pre-wrap" data-testid="ticket-problem-notes">
                  {t.problem_notes || <span className="text-slate-400">—</span>}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Diagnosis</div>
                <div className="text-sm text-slate-900 whitespace-pre-wrap" data-testid="ticket-diagnosis">
                  {t.diagnosis || <span className="text-slate-400">—</span>}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Dispatch ID</div>
                <div className="text-sm text-slate-900 font-mono" data-testid="ticket-dispatch-id">
                  {t.dispatch_id || <span className="text-slate-400 font-sans">—</span>}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Parts needed</div>
                <div className="text-sm text-slate-900 whitespace-pre-wrap" data-testid="ticket-parts-needed">
                  {t.parts_needed || <span className="text-slate-400">—</span>}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Team</div>
                <div className="flex flex-wrap gap-2" data-testid="ticket-team">
                  {(t.team || []).length === 0 && <span className="text-slate-400 text-sm">—</span>}
                  {(t.team || []).map((tid) => {
                    const tech = teamLookup[tid];
                    return (
                      <span key={tid} className="inline-flex items-center gap-1.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-1">
                        {tech?.name || tid}
                      </span>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 max-lg:order-2 lg:order-1">
            <CardContent className="p-6 space-y-4">
              <h2 className="font-heading text-lg font-bold text-slate-900">Appliance</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <Field label="Type" value={findLabel(APPLIANCE_TYPES, t.appliance_type)} />
                <Field label="Manufacturer" value={findLabel(MANUFACTURERS, t.manufacturer)} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Model #</div>
                <div className="text-sm text-slate-900 whitespace-pre-wrap font-mono" data-testid="ticket-model">
                  {t.model_number || <span className="text-slate-400">—</span>}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Serial #</div>
                <div className="text-sm text-slate-900 whitespace-pre-wrap font-mono" data-testid="ticket-serial">
                  {t.serial_number || <span className="text-slate-400">—</span>}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 max-lg:order-7 lg:order-3">
            <CardContent className="p-6 space-y-4">
              <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2" data-testid="parts-title">
                <Package className="w-4 h-4 text-blue-700" />
                Parts
              </h2>
              <PartsPanel ticketId={id} />
            </CardContent>
          </Card>

          <Card className="border-slate-200 max-lg:order-7 lg:order-3">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-slate-900">Attachments</h2>
                <label className="inline-flex items-center gap-2 text-sm bg-blue-700 text-white px-3 py-1.5 rounded-md cursor-pointer hover:bg-blue-800 transition-colors" data-testid="upload-attachment-label">
                  <Upload className="w-4 h-4" />
                  <span>{uploading ? "Uploading…" : "Upload photo / PDF"}</span>
                  <input
                    type="file"
                    accept="image/*,application/pdf,.pdf"
                    capture="environment"
                    multiple
                    className="hidden"
                    onChange={uploadAttachments}
                    disabled={uploading}
                    data-testid="upload-attachment-input"
                  />
                </label>
              </div>
              {(t.attachments || []).length === 0 ? (
                <div className="text-sm text-slate-500">No attachments yet. Tap &ldquo;Upload photo / PDF&rdquo; — on mobile, &ldquo;photo&rdquo; opens the camera directly.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {(t.attachments || []).map((a) => {
                    const isPdf = a.content_type === "application/pdf";
                    const url = `${API}/tickets/${id}/attachments/${a.id}`;
                    return (
                      <div key={a.id} className="relative group rounded-md overflow-hidden border border-slate-200 bg-slate-50" data-testid={`attachment-${a.id}`}>
                        <a href={url} target="_blank" rel="noreferrer" className="block">
                          {isPdf ? (
                            <div className="w-full h-32 flex flex-col items-center justify-center bg-red-50 text-red-700 p-2">
                              <FileText className="w-8 h-8 mb-1" />
                              <span className="text-xs font-medium text-center line-clamp-2 break-all">{a.filename}</span>
                              <span className="text-[10px] text-red-600 mt-0.5">PDF</span>
                            </div>
                          ) : (
                            <img src={url} alt={a.filename} className="w-full h-32 object-cover" loading="lazy" />
                          )}
                        </a>
                        <button
                          type="button"
                          onClick={() => deleteAttachment(a.id)}
                          className="absolute top-1 right-1 bg-white/90 hover:bg-red-50 text-red-700 rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border border-slate-200"
                          title="Delete"
                          data-testid={`delete-attachment-${a.id}`}
                        >
                          <XIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="max-lg:contents lg:space-y-4 lg:order-2">
          <Card className="border-slate-200 max-lg:order-1">
            <CardContent className="p-6 space-y-3">
              <h2 className="font-heading text-lg font-bold text-slate-900">Customer</h2>
              <div className="text-base font-semibold text-slate-900" data-testid="ticket-customer-name">
                {c.first_name} {c.last_name}
              </div>
              {c.phone && (
                <div className="flex items-center justify-between gap-2">
                  <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-sm text-slate-700">
                    <Phone className="w-4 h-4 text-slate-400" /> {c.phone}
                  </a>
                  <div className="flex items-center gap-2">
                    <a href={`tel:${c.phone}`} data-testid="quick-call-btn">
                      <Button variant="outline" size="icon" className="rounded-full h-9 w-9" title="Call customer">
                        <Phone className="w-4 h-4" />
                      </Button>
                    </a>
                    <Link to={`/messages?customer=${c.id}`} data-testid="quick-message-btn">
                      <Button variant="outline" size="icon" className="rounded-full h-9 w-9" title="Text customer">
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
              {c.email && (
                <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-sm text-slate-700">
                  <Mail className="w-4 h-4 text-slate-400" /> {c.email}
                </a>
              )}
              {(c.street || c.city || c.state || c.zip_code) && (
                <div className="flex items-start gap-2 text-sm text-slate-700">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    {c.street && <div>{c.street}</div>}
                    <div>{[c.city, c.state, c.zip_code].filter(Boolean).join(", ")}</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 max-lg:order-8">
            <CardContent className="p-6 space-y-3">
              <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2" data-testid="job-history-title">
                <History className="w-4 h-4 text-blue-700" />
                Job history
              </h2>
              <p className="text-xs text-slate-500 -mt-1">Past visits for {c.first_name} {c.last_name}</p>
              <JobHistory customerId={c.id} excludeTicketId={t.id} limit={5} />
            </CardContent>
          </Card>

          <Card className="border-slate-200 max-lg:order-3">
            <CardContent className="p-6 space-y-3">
              <h2 className="font-heading text-lg font-bold text-slate-900">Update status</h2>
              <Select value={t.status} onValueChange={updateStatus}>
                <SelectTrigger data-testid="quick-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card className="border-slate-200 max-lg:order-3">
            <CardContent className="p-6 space-y-3">
              <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2" data-testid="followup-title">
                <Flag className="w-4 h-4 text-amber-600" />
                Follow-up
              </h2>
              <FollowupPanel ticket={t} onChange={load} />
            </CardContent>
          </Card>

          <Card className="border-slate-200 max-lg:order-6">
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-slate-900">Confirmation email</h2>
                {t.confirmation_email_sent_at && (
                  <span className="text-[11px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                    Sent
                  </span>
                )}
              </div>
              {c.email ? (
                <>
                  <p className="text-sm text-slate-500">
                    Sends an approval &amp; appointment reminder to <span className="font-medium text-slate-700">{c.email}</span>.
                  </p>
                  <Textarea
                    rows={3}
                    placeholder="Optional note to include (e.g. 'Our tech Justin will call 30 minutes before arrival.')"
                    value={emailNote}
                    onChange={(e) => setEmailNote(e.target.value)}
                    data-testid="confirmation-email-note"
                  />
                  <Button
                    onClick={sendConfirmationEmail}
                    disabled={sendingEmail}
                    className="w-full bg-blue-700 hover:bg-blue-800 gap-2"
                    data-testid="send-confirmation-btn"
                  >
                    <Send className="w-4 h-4" />
                    {sendingEmail
                      ? "Sending…"
                      : t.confirmation_email_sent_at
                      ? "Send again"
                      : "Send confirmation"}
                  </Button>
                  {t.confirmation_email_sent_at && (
                    <p className="text-xs text-slate-500">
                      Last sent {formatDateTime(t.confirmation_email_sent_at)}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500">
                  No email on file for this customer. Add one on the customer record to enable
                  confirmation emails.
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 max-lg:order-5">
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-blue-700" />
                  Send SMS
                </h2>
                {(t.sms_log || []).length > 0 && (
                  <span className="text-[11px] uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5" data-testid="sms-count">
                    {(t.sms_log || []).length} sent
                  </span>
                )}
              </div>
              {c.phone ? (
                <>
                  <p className="text-sm text-slate-500">
                    Text <span className="font-medium text-slate-700">{c.phone}</span>
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={loadOnTheWayTemplate}
                      disabled={loadingTemplate}
                      className="gap-1.5"
                      data-testid="on-the-way-btn"
                    >
                      <Truck className="w-3.5 h-3.5" />
                      {loadingTemplate ? "Loading…" : "On the way"}
                    </Button>
                  </div>
                  <Textarea
                    rows={4}
                    placeholder="Type a message to the customer…"
                    value={smsBody}
                    onChange={(e) => setSmsBody(e.target.value)}
                    data-testid="sms-body"
                  />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-400">{smsBody.length} chars</span>
                    <Button
                      onClick={sendSms}
                      disabled={sendingSms || !smsBody.trim()}
                      className="bg-blue-700 hover:bg-blue-800 gap-2"
                      data-testid="send-sms-btn"
                    >
                      <Send className="w-4 h-4" />
                      {sendingSms ? "Sending…" : "Send SMS"}
                    </Button>
                  </div>
                  {(t.sms_log || []).length > 0 && (
                    <div className="pt-2 border-t border-slate-100 space-y-2" data-testid="sms-log">
                      <div className="text-xs uppercase tracking-wider text-slate-500">Recent messages</div>
                      {(t.sms_log || []).slice().reverse().slice(0, 3).map((s) => (
                        <div key={s.sid} className="text-xs bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5">
                          <div className="text-slate-500">{formatDateTime(s.sent_at)}</div>
                          <div className="text-slate-800 whitespace-pre-wrap mt-0.5">{s.body}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500">
                  No phone on file for this customer. Add one on the customer record to enable SMS.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {isAdmin && (
        <ChargeOnReaderDialog
          open={readerOpen}
          onOpenChange={setReaderOpen}
          ticket={t}
        />
      )}
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-sm text-slate-900 mt-0.5">{value}</div>
  </div>
);
